import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowPathIcon, StopIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { drawRawVideo, drawSegmentedVideo, loadSelfieSegmenter, VideoBackground } from './segmenter';
import { uploadRecordingForToken, UploadProgress } from './uploader';

type RecorderContext = {
  candidate_first_name: string;
  candidate_display_name: string;
  expires_at: string;
  single_use: boolean;
  script: {
    content: string;
    pronunciation_phonetic?: string | null;
  };
  branding: {
    company_name: string;
    background_templates: VideoBackground[];
  };
};

const CANVAS_WIDTH = 1280;
const CANVAS_HEIGHT = 720;

function supportedMimeType() {
  const options = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
  return options.find(option => MediaRecorder.isTypeSupported(option)) || '';
}

export default function RecorderPage() {
  const { requestToken } = useParams();
  const sourceVideoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationRef = useRef<number | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingStartRef = useRef<number | null>(null);
  const backgroundRef = useRef<VideoBackground>('studio');

  const [context, setContext] = useState<RecorderContext | null>(null);
  const [background, setBackground] = useState<VideoBackground>('studio');
  const [status, setStatus] = useState<'loading' | 'ready' | 'camera' | 'recording' | 'preview' | 'submitting' | 'done' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);

  useEffect(() => {
    backgroundRef.current = background;
  }, [background]);

  useEffect(() => {
    let mounted = true;
    AXIOS_INSTANCE.get(`/r/${requestToken}/context`)
      .then(response => {
        if (!mounted) return;
        setContext(response.data);
        setBackground(response.data.branding?.background_templates?.[0] || 'studio');
        setStatus('ready');
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err?.response?.data?.detail || 'This recording link is unavailable.');
        setStatus('error');
      });
    return () => {
      mounted = false;
    };
  }, [requestToken]);

  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      streamRef.current?.getTracks().forEach(track => track.stop());
    };
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const renderLoop = async (stream: MediaStream) => {
    const video = sourceVideoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!video || !canvas || !ctx) return;
    video.srcObject = stream;
    await video.play();
    const segmenter = await loadSelfieSegmenter();

    const draw = () => {
      if (!video.videoWidth) {
        animationRef.current = requestAnimationFrame(draw);
        return;
      }
      try {
        drawSegmentedVideo(ctx, video, CANVAS_WIDTH, CANVAS_HEIGHT, backgroundRef.current, segmenter);
      } catch {
        drawRawVideo(ctx, video, CANVAS_WIDTH, CANVAS_HEIGHT, backgroundRef.current);
      }
      animationRef.current = requestAnimationFrame(draw);
    };
    draw();
  };

  const startCamera = async () => {
    setError(null);
    setStatus('camera');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: CANVAS_WIDTH, height: CANVAS_HEIGHT, facingMode: 'user' },
        audio: true,
      });
      streamRef.current = stream;
      await renderLoop(stream);
    } catch {
      setError('Camera or microphone access was not granted.');
      setStatus('ready');
    }
  };

  const startRecording = () => {
    const canvas = canvasRef.current;
    const sourceStream = streamRef.current;
    if (!canvas || !sourceStream) return;
    if (typeof MediaRecorder === 'undefined') {
      setError('This browser does not support in-browser recording. Upload a video file instead.');
      setStatus('ready');
      return;
    }
    chunksRef.current = [];
    const canvasStream = canvas.captureStream(30);
    sourceStream.getAudioTracks().forEach(track => canvasStream.addTrack(track));
    const mimeType = supportedMimeType();
    const recorder = new MediaRecorder(canvasStream, mimeType ? { mimeType } : undefined);
    recorder.ondataavailable = event => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' });
      setRecordedBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
      setStatus('preview');
    };
    recorderRef.current = recorder;
    recordingStartRef.current = performance.now();
    recorder.start(1000);
    setStatus('recording');
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
  };

  const submitBlob = async (blob: Blob, uploadFilename?: string) => {
    if (!requestToken) {
      setError('This recording link is unavailable.');
      return;
    }
    setError(null);
    setStatus('submitting');
    setUploadProgress({
      uploaded: 0,
      total: blob.size,
      partsCompleted: 0,
      partsTotal: 1,
      multipart: false,
    });
    try {
      const filename = uploadFilename || `candidate-thank-you-${Date.now()}.webm`;
      const result = await uploadRecordingForToken(
        requestToken,
        filename,
        blob,
        (progress) => setUploadProgress(progress),
      );
      const durationSeconds = recordingStartRef.current
        ? Math.max(0, (performance.now() - recordingStartRef.current) / 1000)
        : undefined;
      await AXIOS_INSTANCE.post(`/r/${requestToken}/submit`, {
        storage_uri: result.storageUri,
        content_type: result.contentType,
        file_size_bytes: result.fileSize,
        duration_seconds: durationSeconds,
        background_template: background,
      });
      setStatus('done');
      streamRef.current?.getTracks().forEach(track => track.stop());
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Video submission failed.');
      setStatus('preview');
    } finally {
      setUploadProgress(null);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError(null);
    setRecordedBlob(file);
    setPreviewUrl(URL.createObjectURL(file));
    setStatus('preview');
  };

  const reRecord = async () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setRecordedBlob(null);
    recordingStartRef.current = null;
    streamRef.current?.getTracks().forEach(track => track.stop());
    await startCamera();
  };

  if (status === 'loading') {
    return <div className="min-h-screen grid place-items-center bg-gray-50 text-charcoal">Loading...</div>;
  }

  if (status === 'error') {
    return <div className="min-h-screen grid place-items-center bg-gray-50 p-6 text-charcoal">{error}</div>;
  }

  return (
    <main className="min-h-screen bg-gray-50 text-charcoal">
      <div className="mx-auto flex min-h-screen max-w-6xl flex-col px-6 py-8">
        <header className="mb-6 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500">{context?.branding.company_name}</p>
            <h1 className="font-title text-3xl text-charcoal">Thank you, {context?.candidate_first_name}</h1>
          </div>
          <Badge variant={status === 'done' ? 'success' : 'secondary'}>{status}</Badge>
        </header>

        {error && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            {error}
          </div>
        )}

        <div className="grid flex-1 gap-6 lg:grid-cols-[360px_1fr]">
          <aside className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="font-subtitle text-xl text-charcoal">Script</h2>
            {context?.script.pronunciation_phonetic && (
              <p className="mt-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                {context.script.pronunciation_phonetic}
              </p>
            )}
            <p className="mt-4 whitespace-pre-line text-lg leading-8 text-charcoal">
              {context?.script.content}
            </p>
            <label className="mt-6 block">
              <span className="text-sm text-gray-500">Upload file</span>
              <input className="mt-2 block w-full text-sm" type="file" accept="video/*" onChange={handleFileUpload} />
            </label>
          </aside>

          <section className="flex min-h-[520px] flex-col rounded-lg border border-gray-200 bg-white">
            <div className="relative aspect-video w-full overflow-hidden rounded-t-lg bg-gray-100">
              <video ref={sourceVideoRef} className="hidden" muted playsInline />
              <canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} className="h-full w-full object-cover" />
              {previewUrl && <video src={previewUrl} className="absolute inset-0 h-full w-full bg-black object-contain" controls playsInline />}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 p-4">
              <div className="flex items-center gap-2">
                {(['studio', 'soft_blue', 'warm_gray'] as VideoBackground[]).map(option => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setBackground(option)}
                    className={`h-8 w-8 rounded-full border ${background === option ? 'border-charcoal ring-2 ring-charcoal/20' : 'border-gray-200'}`}
                    style={{ background: option === 'studio' ? '#f7f8f9' : option === 'soft_blue' ? '#e7f0f4' : '#ebe7df' }}
                    title={option.replace('_', ' ')}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                {status === 'ready' && <Button onClick={startCamera}><VideoCameraIcon />Start</Button>}
                {status === 'camera' && <Button variant="brand" onClick={startRecording}><VideoCameraIcon />Record</Button>}
                {status === 'recording' && <Button variant="destructive" onClick={stopRecording}><StopIcon />Stop</Button>}
                {status === 'preview' && <Button variant="outline" onClick={reRecord}><VideoCameraIcon />Re-record</Button>}
                {status === 'preview' && recordedBlob && (
                  <Button
                    variant="brand"
                    onClick={() => submitBlob(recordedBlob, recordedBlob instanceof File ? recordedBlob.name : undefined)}
                  >
                    <ArrowPathIcon />Submit
                  </Button>
                )}
                {status === 'submitting' && <Button disabled><ArrowPathIcon className="animate-spin" />Submitting</Button>}
              </div>
            </div>
          </section>
        </div>

        {status === 'submitting' && uploadProgress && uploadProgress.total > 0 && (
          <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between text-sm text-charcoal">
              <span>
                {uploadProgress.multipart
                  ? `Uploading part ${uploadProgress.partsCompleted}/${uploadProgress.partsTotal}`
                  : 'Uploading'}
              </span>
              <span className="text-gray-500">
                {Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%
              </span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded bg-gray-100">
              <div
                className="h-full bg-charcoal transition-[width] duration-200"
                style={{
                  width: `${Math.min(100, Math.max(0, (uploadProgress.uploaded / uploadProgress.total) * 100))}%`,
                }}
              />
            </div>
          </div>
        )}

        {status === 'done' && (
          <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
            Your video was submitted.
          </div>
        )}
      </div>
    </main>
  );
}
